import Util from '@/assets/libs/util.js'
import router from '@/router'
import eventModule from '@/assets/libs/eventModule'
import UpdaterController from '@/assets/controllers/UpdaterController'
import requestModule from '@/assets/libs/requestModule'
import LocaleController from '@/assets/controllers/LocaleController'

const MAINTENANCE_PATH = '/maintenance'
let message = ''
let wsSocket = null
let wsShouldReconnect = true
let wsReconnectTimer = null
let wsConnected = false
let terminalBlocked = false
let pendingServerInstanceReload = false
let webSocketToken = process.env.WEBSOCKET_TOKEN
let webSocketPort = null
let updateMenuInProgress = false

const MAINTENANCE_ALLOWED_PATHS = ['/maintenance', '/login', '/config']

const setProgress = function (progress, progressMessage) {
    eventModule.$emit('setProgress', progress, progressMessage)
}

const handleProgressMessage = function (data) {
    if (!updateMenuInProgress || data.progress == null) {
        return
    }
    const config = getConfig()
    if (data.posCode && config && data.posCode !== config.caixaCodigo) {
        return
    }
    setProgress(data.progress, data.progressMessage || '')
}

const getBaseConfig = async function () {
    return requestModule.postRequest('getBaseConfig', { type: 'basic' }).then(confData => {
        webSocketPort = confData.websocket && confData.websocket.port
        return true
    }).catch(() => {
        return false
    })
}

const getWebSocketAddress = function () {
    const server = window.location.hostname
    const protocol = (window.location.protocol === 'https:') ? 'wss' : 'ws'
    return `${protocol}://${server}:${webSocketPort}`
}

const navigateToMaintenance = function () {
    if (router.currentRoute.path !== MAINTENANCE_PATH) {
        router.push(MAINTENANCE_PATH)
    }
}

const navigateFromMaintenance = function () {
    terminalBlocked = false
    if (router.currentRoute.path === MAINTENANCE_PATH) {
        router.push('/')
    }
}

const isConnected = function () {
    return wsConnected
}

const isTerminalAvailable = function () {
    if (Util.isOracle()) {
        return true // se for oracle sempre retorna true, pois o oracle não usa websocket
    }
    return wsConnected && !terminalBlocked
}

const isMaintenanceAllowedPath = function (path) {
    return MAINTENANCE_ALLOWED_PATHS.includes(path)
}

const registerClient = async function () {
    const generalConfigs = Util.getGeneralInfo()
    eventModule.$emit('updateBaseConfig')
    sendMessage({
        type: 'register_client',
        posCode: generalConfigs.posCode,
        subsidiaryCode: generalConfigs.subsidiaryCode,
        storeCode: generalConfigs.storeCode
    })
}

const getClientId = function () {
    let clientId = Util.getLocalVar('clientId')
    if (!clientId) {
        const rand = new Uint32Array(1)
        crypto.getRandomValues(rand)
        clientId = `${Date.now()}-${rand[0].toString(16)}`
        // clientId = `${Date.now()}-${crypto.randomInt(1000, 9999).toString()}`
        Util.setLocalVar('clientId', clientId)
    }
    return clientId
}

const handleServerInstanceMessage = async function (data) {
    const serverInstanceId = Util.getLocalVar('serverInstanceId')
    const version = Util.getLocalVar('version')

    if (version && version !== data.version) {
    // if (version) {
        Util.setLocalVar('serverInstanceId', data.instanceId)
        Util.setLocalVar('version', data.version)
        setMessage('Reconfigurando terminal...')
        const tasksSucceeded = await handleTasks()
        if (tasksSucceeded) {
            applyServerInstanceReload()
        }
    } else if (serverInstanceId && serverInstanceId !== data.instanceId) {
        Util.setLocalVar('serverInstanceId', data.instanceId)
        applyServerInstanceReload()
    } else {
        if (!serverInstanceId) {
            Util.setLocalVar('serverInstanceId', data.instanceId)
        }
        if (!version) {
            Util.setLocalVar('version', data.version)
        }
        navigateFromMaintenance()
    }
}

const applyServerInstanceReload = function () {
    if (router.currentRoute.path !== '/') {
        router.push('/')
    }
    window.location.reload()
}

const clientAuthorized = async function () {
    wsConnected = true
    await registerClient()
}

const handleError = async function (data) {
    if (data.message === 'pos_code_duplicate') {
        terminalBlocked = true
        setMessage('Código de caixa duplicado')
        navigateToMaintenance()
    }
    if (data.message === 'pos_code_missing') {
        terminalBlocked = true
        setMessage('Configurações incompletas')
        navigateToMaintenance()
    }
    if (data.message === 'menucache_error_no_ssts') {
        terminalBlocked = true
        setMessage('Erro ao buscar cardápio, verifique se as configurações estão corretas e se a interface está acessível e reinicie o serviço de menuCache')
        navigateToMaintenance()
    }
}

const messageHandlers = {
    server_instance: handleServerInstanceMessage,
    client_authorized: clientAuthorized,
    error: handleError,
    progress: handleProgressMessage
}

const handleMessage = function (event) {
    try {
        const data = JSON.parse(event.data)
        const handler = messageHandlers[data.type]
        if (handler) {
            handler(data)
        }
    } catch (error) {
        console.log('Erro ao processar mensagem WebSocket:', error)
    }
}
const sendMessage = function (message) {
    if (wsSocket) {
        wsSocket.send(JSON.stringify(message))
    }
}
const connectWebSocket = async function () {
    if (!wsShouldReconnect) {
        return
    }

    if (wsSocket) {
        const state = wsSocket.readyState
        if (state === WebSocket.CONNECTING || state === WebSocket.OPEN) {
            return
        }
        wsSocket.onclose = null
        wsSocket.onerror = null
        wsSocket = null
    }

    wsConnected = false
    try {
        let baseConfig = await getBaseConfig()
        if (!baseConfig || !webSocketToken || !webSocketPort) {
            blockNavigateToMaintenanceAndScheduleReconnect()
            return
        }
        const ws = new WebSocket(getWebSocketAddress())
        wsSocket = ws
        ws.onopen = async () => {
            clearWebSocketReconnectTimer()
            ws.send(JSON.stringify({
                type: 'authenticate',
                token: webSocketToken,
                clientId: getClientId()
            }))
        }
        ws.onmessage = handleMessage
        ws.onclose = () => {
            if (wsSocket !== ws) {
                return
            }
            blockNavigateToMaintenanceAndScheduleReconnect()
        }
        ws.onerror = (event) => {
            console.log('Erro no WebSocket:', event)
        }
    } catch (error) {
        blockNavigateToMaintenanceAndScheduleReconnect()
        console.log('Erro ao conectar ao WebSocket:', error)
    }
}

const blockNavigateToMaintenanceAndScheduleReconnect = function () {
    wsSocket = null
    wsConnected = false
    terminalBlocked = false
    setMessage('Servidor desconectado')
    navigateToMaintenance()
    scheduleWebSocketReconnect()
}

const scheduleWebSocketReconnect = function () {
    if (!wsShouldReconnect || wsReconnectTimer) {
        return
    }
    wsReconnectTimer = setTimeout(() => {
        wsReconnectTimer = null
        connectWebSocket()
    }, 1000)
}

const clearWebSocketReconnectTimer = function () {
    if (wsReconnectTimer) {
        clearTimeout(wsReconnectTimer)
        wsReconnectTimer = null
    }
}

const disconnectWebSocket = function () {
    wsShouldReconnect = false
    clearWebSocketReconnectTimer()
    if (wsSocket) {
        wsSocket.onclose = null
        wsSocket.onerror = null
        wsSocket.close()
        wsSocket = null
    }
}

const getString = function (key) {
    return LocaleController.getString(key)
}

const getConfig = function () {
    return Util.getLocalVar('taaconfig')
}

const delay = function (seconds) {
    return new Promise(function (resolve) {
        setTimeout(resolve, seconds * 1000)
    })
}

const buildParamErrorsMessage = function (result) {
    let message = ''
    Object.keys(result).forEach(function (key) {
        if (result[key]) {
            result[key].forEach(function (error) {
                message += (error.error + ' - ' + error.description + ' | ')
            })
        }
    })
    return message
}

const syncAllConfigs = async function (config) {
    const result = await UpdaterController.updatePosConf(config)
    return {
        status: result,
        message: result ? getString('concluido') : getString('falha')
    }
}

const checkConectionStatus = async function () {
    const minVersionSupported = process.env.minVersionSupported
    const method = Util.isOracle() ? 'get' : 'post'
    let resultOdhenInterface = await requestModule.postRequest('/', {}, method)
        .then((e) => {
            if (!e || !e.data) {
                return {
                    error: true,
                    status: false,
                    message: getString('interface_nao_encontrada_404')
                }
            }
            if (e.data && !e.data.connection) {
                return {
                    error: true,
                    status: false,
                    message: getString('interface_sem_conexao')
                }
            }
            return {
                error: false,
                status: true,
                message: `${getString('versao_interface')} ${e.data.version}`,
                version: e.data.version
            }
        })
        .catch(() => {
            return {
                error: true,
                message: getString('interface_nao_encontrada_404')
            }
        })
    let message = resultOdhenInterface.message
    if (resultOdhenInterface.error) {
        return {
            status: false,
            message: message
        }
    }
    let interfaceVersionIsSupported = Util.interfaceVersionIsSupported(resultOdhenInterface.version, minVersionSupported)
    if (!interfaceVersionIsSupported) {
        let statusVersion = getString('versao_interface') + `(${resultOdhenInterface.version})` +
            getString('incompativel_com_o_TAA_necessario_versao') + minVersionSupported +
            getString('ou_superior')
        return {
            status: false,
            message: statusVersion
        }
    }
    return {
        status: true,
        message: message
    }
}

const checkParamns = async function (config) {
    let result = await UpdaterController.checkParams(config)
    if (!result) {
        return {
            status: true,
            message: getString('concluido')
        }
    }
    let errorMessage = buildParamErrorsMessage(result)
    if (errorMessage) {
        return {
            status: false,
            message: errorMessage
        }
    }
    return {
        status: true,
        message: getString('concluido')
    }
}

const getTiposRecebimento = async function (config) {
    const result = await UpdaterController.updatePaymentsTypes(config)
    return {
        status: result,
        message: result ? getString('concluido') : getString('falha')
    }
}

const updateMenu = async function (config) {
    let handleTask = true
    if (process.env.NODE_ENV !== 'production') {
        await Util.showConfirmationDialogSync(null, () => {
            handleTask = true
        }, 'Atualizar cardápio?', () => {
            handleTask = false
        })
    }
    if (!handleTask) {
        return {
            status: false,
            message: getString('cancelado')
        }
    }
    const tasks = buildTasks()
    const progressStart = ((tasks.length - 1) / tasks.length) * 100
    updateMenuInProgress = true
    let result
    try {
        result = await UpdaterController.updateMenu(true, config, {
            reportProgress: true,
            progressStart,
            progressEnd: 100
        })
    } finally {
        updateMenuInProgress = false
    }
    Util.updateLastSync()
    if (result) {
        result = await UpdaterController.updateNewSuggestions(true)
    }
    return {
        status: result,
        message: result ? getString('concluido') : getString('falha')
    }
}

const taskHandlers = {
    checkConectionStatus,
    syncAllConfigs,
    checkParamns,
    getTiposRecebimento,
    updateMenu
}

const buildTasks = function () {
    return [
        {
            task: 'checkConectionStatus',
            name: getString('verificar_conexao'),
            message: getString('pendente'),
            status: null,
            process: true
        },
        {
            task: 'syncAllConfigs',
            name: getString('atualizar_informações_caixa'),
            message: getString('pendente'),
            status: null,
            process: true
        },
        {
            task: 'checkParamns',
            name: getString('verificar_params'),
            message: getString('pendente'),
            status: null,
            process: true
        },
        {
            task: 'getTiposRecebimento',
            name: getString('atualizar_recebimentos'),
            message: getString('pendente'),
            status: null,
            process: true
        },
        {
            task: 'updateMenu',
            name: getString('atualizar_cardapio'),
            message: getString('pendente'),
            status: null,
            process: true
        }
    ]
}

const handleTasks = async function () {
    let hasError = false
    const config = getConfig()
    const tasks = buildTasks()
    eventModule.$emit('setCountDownTimeOut', false)
    if (!config || !config.caixaCodigo || !config.filialCodigo || !config.lojaCodigo) {
        return false
    }
    try {
        for (let i = 0; i < tasks.length; i++) {
            if (hasError) break
            if (!tasks[i].process) continue
            tasks[i].message = getString('processando')
            setMessage(tasks[i].name + ': ' + getString('processando'))
            let handler = taskHandlers[tasks[i].task]
            let result = await handler(config)
            tasks[i].status = result.status || false
            tasks[i].message = result.message || ''
            eventModule.$emit('setProgress', (i + 1) / tasks.length * 100, '')
            if (tasks[i].status === false && process.env.NODE_ENV === 'production') {
                hasError = true
                setMessage(tasks[i].name + ': ' + tasks[i].message)
                break
            }
        }
    } catch (e) {
        hasError = true
        setMessage(getString('falha'))
        Util.logComunication('Exception - WebsocketController handleTasks', {}, e)
    }
    eventModule.$emit('setCountDownTimeOut', true)

    if (!hasError) {
        setMessage(getString('concluido'))
        let cont = 3
        while (cont > 0) {
            await delay(1)
            cont--
        }
        Util.reloadcachedConfigs()
        window.location.reload()
        return true
    }
    return false
}

const reconfigureTerminal = async function () {
    navigateToMaintenance()
    setMessage('Reconfigurando terminal...')
    pendingServerInstanceReload = false
    const tasksSucceeded = await handleTasks()

    if (!tasksSucceeded && pendingServerInstanceReload) {
        pendingServerInstanceReload = false
        applyServerInstanceReload()
    }
}

const getMessage = function () {
    return message
}

const setMessage = function (msg) {
    if (msg !== message) {
        message = msg
        eventModule.$emit('setMessage', msg)
    }
}
export default {
    connectWebSocket,
    disconnectWebSocket,
    isConnected,
    isTerminalAvailable,
    isMaintenanceAllowedPath,
    reconfigureTerminal,
    getMessage,
    setMessage,
    setProgress
}



// WEBPACK FOOTER //
// ./src/assets/controllers/WebsocketController.js